import { DateTime } from "luxon";
import { type ExactShiftFilterType, isSelectedTimeFrameValidForShiftFilter, type ShiftFilterType } from "../../../components/staffing/MonthlyView/utils";
import type { Department } from "../../../contexts/AuthProvider";
import { type DepartmentLocation, type Nurse, NurseFullRole, NurseFullRoleShort, NurseRole, type RecurringShift } from "../../../utils/models";
import type { SortingState } from "../../../utils/multiColumnSorting";
import { getShiftIndexByShiftTimes, nursePreferencesToDaysOffAndPto } from "../../../utils/utilFunctions";
import {
	finalizeSectionMap,
	locationMatchesShiftName,
	locationMatchesShiftRoleOrJob,
	locationMatchesShiftTimes,
	type RecurringShiftInCell,
	type ScheduleWeekData,
	type Section,
	type SectionLocationMap,
} from "../v2/utils";
import { isConsecutiveWorkingDay } from "./consecutiveDateMath";
import { CYCLE_BLOCKS_SHOWN } from "./consts";

/**
 * Pure builder for the Consecutive Master Schedule grid.
 *
 * Mirrors `buildScheduleData` (MasterScheduleV2) — same `SectionLocationMap` output shape, same
 * section→location grouping, filter predicates, PTO enrichment and sorting — but:
 *   - the grid is `CYCLE_BLOCKS_SHOWN` blocks × `daysPerBlock` consecutive days (not 7 weekdays × N weeks);
 *   - placement uses the consecutive per-shift modulo (`isConsecutiveWorkingDay`) instead of weekly interval math;
 *   - it only handles `recurringType === "ConsecutiveDays"` (bi-weekly / 3-weekly are Weekly+interval and are
 *     excluded upstream at fetch time).
 *
 * It performs NO side effects — unplaceable shifts are returned as `placementErrors`, letting the
 * caller derive the grid with a `useMemo` (see `useConsecutiveMasterScheduleState`).
 */

export type BuildConsecutiveScheduleDataParams = {
	shifts: RecurringShift[] | undefined;
	chosenDepartment: Department;
	/** Cycle-aligned date shown in the first (Week 1, Day 1) cell. */
	cycleStartDate: DateTime;
	/** Day columns per block (= the selected period's cycle length: working + rest days). */
	daysPerBlock: number;
	/** Department timezone — the frame the per-day cycle math is evaluated in. */
	timezone: string;
	/** Today (dept timezone, start of day) — used only to mark not-yet-started recurrences as "future". */
	today: DateTime;
	jobManagementEnabled: boolean;
	selectedRoleFilters: (NurseRole | "all")[];
	selectedJobFilters: string[];
	selectedLocationFilters: string[];
	selectedShiftFilter: ShiftFilterType | ExactShiftFilterType;
	nurseFilterId: number | null;
	masterScheduleSortState: SortingState<DepartmentLocation>[];
	sectionOrder: Section[];
	customStringOrderMap: Map<string, number> | null;
	getNurseById: (id: number) => Nurse | undefined;
	getNurseFormattedName: (nurseId: number) => string;
	longPtoMinimumDays: number;
};

export type ConsecutivePlacementError = { section: string; recurringShiftId: number | undefined; location: string | undefined };

export type BuildConsecutiveScheduleDataResult = {
	data: SectionLocationMap;
	placementErrors: ConsecutivePlacementError[];
};

/** Builds the empty `weeklyShifts` scaffold: `CYCLE_BLOCKS_SHOWN` blocks × `daysPerBlock` consecutive days. */
const buildEmptyBlocks = (cycleStartDate: DateTime, daysPerBlock: number): ScheduleWeekData[] =>
	Array.from({ length: CYCLE_BLOCKS_SHOWN }, (_, blockIndex) => ({
		weekNumber: blockIndex + 1,
		days: Array.from({ length: daysPerBlock }, (_, dayIndex) => ({
			dayNumber: dayIndex + 1,
			recurringShiftsInCell: [] as RecurringShiftInCell[],
			dayDate: cycleStartDate.plus({ days: blockIndex * daysPerBlock + dayIndex }),
		})),
	}));

export const buildConsecutiveScheduleData = (params: BuildConsecutiveScheduleDataParams): BuildConsecutiveScheduleDataResult => {
	const {
		shifts,
		chosenDepartment,
		cycleStartDate,
		daysPerBlock,
		timezone,
		today,
		jobManagementEnabled,
		selectedRoleFilters,
		selectedJobFilters,
		selectedLocationFilters,
		selectedShiftFilter,
		nurseFilterId,
		masterScheduleSortState,
		sectionOrder,
		customStringOrderMap,
		getNurseById,
		getNurseFormattedName,
		longPtoMinimumDays,
	} = params;

	const data: SectionLocationMap = {};
	const placementErrors: ConsecutivePlacementError[] = [];

	const passesConfigRoleOrJobFilter = (loc: DepartmentLocation): boolean =>
		jobManagementEnabled
			? !loc.jobId || selectedJobFilters.includes(loc.jobId)
			: loc.roles.some((role) => role === "all" || selectedRoleFilters.includes(NurseRole[NurseFullRoleShort[role]]));

	// 1. Scaffold empty location rows for every configured location that passes the filters.
	const allLocationSections = new Set([...chosenDepartment.locations.map((loc) => loc.section), "" /* always keep an empty section */]);
	for (const locationSection of allLocationSections) {
		if (!data[locationSection]) data[locationSection] = [];
		for (const departmentLocationConfig of chosenDepartment.locations.filter((loc) => loc.section === locationSection)) {
			const passesLocationFilter = selectedLocationFilters.length === 0 || selectedLocationFilters.includes(departmentLocationConfig.location ?? "");
			if (!(passesConfigRoleOrJobFilter(departmentLocationConfig) && passesLocationFilter)) continue;

			const startTime = DateTime.fromFormat(departmentLocationConfig.shift_start.substring(0, 5), "HH:mm");
			const endTime = DateTime.fromFormat(departmentLocationConfig.shift_end.substring(0, 5), "HH:mm");
			if (!isSelectedTimeFrameValidForShiftFilter({ startTime, endTime, selectedShiftFilter, dayStart: chosenDepartment.dayStart })) continue;

			data[locationSection].push({
				location: {
					...departmentLocationConfig,
					shift_start: departmentLocationConfig.shift_start.substring(0, 5),
					shift_end: departmentLocationConfig.shift_end.substring(0, 5),
				},
				weeklyShifts: buildEmptyBlocks(cycleStartDate, daysPerBlock),
				shiftType: chosenDepartment.shift_types[getShiftIndexByShiftTimes({ startTime, endTime })],
			});
		}
	}

	// 2. Place each consecutive shift into the scaffolded grid.
	const placeShift = (recurringShift: RecurringShift): void => {
		if (recurringShift.recurringParams.recurringType !== "ConsecutiveDays") return;
		const { working_days_amount: workingDaysAmount, rest_days_amount: restDaysAmount } = recurringShift.recurringParams;

		const recurrenceStart = DateTime.fromFormat(recurringShift.startDate, "yyyy-MM-dd", { zone: timezone });
		const recurrenceEnd = recurringShift.endDate ? DateTime.fromFormat(recurringShift.endDate, "yyyy-MM-dd", { zone: timezone }) : null;
		const recurrenceStartTime = DateTime.fromFormat(recurringShift.shiftStart.substring(0, 5), "HH:mm", { zone: timezone });
		const rawEndTime = DateTime.fromFormat(recurringShift.shiftEnd.substring(0, 5), "HH:mm", { zone: timezone });
		const recurrenceEndTime = rawEndTime.hour <= recurrenceStartTime.hour ? rawEndTime.plus({ day: 1 }) : rawEndTime;

		if (
			!isSelectedTimeFrameValidForShiftFilter({
				startTime: recurrenceStartTime,
				endTime: recurrenceEndTime,
				selectedShiftFilter,
				dayStart: chosenDepartment.dayStart,
			})
		) {
			return;
		}

		const foundNurse = getNurseById(recurringShift.nurseId);
		if (!foundNurse) return;
		const relevantRole = NurseRole[NurseFullRoleShort[foundNurse.role]];
		const locationOfRecurringShift = recurringShift.location ?? "";

		const relevantLocations = chosenDepartment.locations.filter(
			(loc) => locationMatchesShiftName(loc, recurringShift) && locationMatchesShiftRoleOrJob(loc, recurringShift, relevantRole, jobManagementEnabled),
		);
		const relevantLocationsByTime = relevantLocations.filter((loc) => locationMatchesShiftTimes(loc, recurringShift));
		const relevantSection =
			(relevantLocationsByTime.length > 0 ? relevantLocationsByTime : relevantLocations)
				.map((loc) => loc.section)
				.sort()
				.at(0) ?? "";
		const sectionIfEmptySectionNotExists =
			chosenDepartment.locations.find((loc) => locationMatchesShiftName(loc, recurringShift) && locationMatchesShiftTimes(loc, recurringShift))?.section ?? "";
		const discreetSection = relevantSection || sectionIfEmptySectionNotExists;
		const currentData = data[discreetSection];
		if (!currentData) {
			placementErrors.push({ section: discreetSection, recurringShiftId: recurringShift.id, location: recurringShift.location });
			return;
		}

		let locationData = currentData.find(
			(entry) =>
				locationMatchesShiftName(entry.location, recurringShift) &&
				locationMatchesShiftTimes(entry.location, recurringShift) &&
				locationMatchesShiftRoleOrJob(entry.location, recurringShift, relevantRole, jobManagementEnabled),
		);
		let shouldAddLocationData = false;

		if (!locationData) {
			// No configured location matches these times — synthesize an "outside of config" row (red *).
			const newLocationData: Partial<DepartmentLocation> = {
				location: locationOfRecurringShift,
				shift_start: recurringShift.shiftStart,
				shift_end: recurringShift.shiftEnd,
				roles: [NurseFullRole[relevantRole]],
				department_id: chosenDepartment.department_id,
				section: discreetSection,
				days: ["sun", "mon", "tue", "wed", "thu", "fri", "sat"],
				...(jobManagementEnabled && recurringShift.jobId ? { jobId: recurringShift.jobId } : {}),
			};
			locationData = {
				location: newLocationData as DepartmentLocation,
				isLocationOutsideOfConfig: true,
				weeklyShifts: buildEmptyBlocks(cycleStartDate, daysPerBlock),
				shiftType:
					chosenDepartment.shift_types[
						getShiftIndexByShiftTimes({
							startTime: DateTime.fromFormat((newLocationData.shift_start ?? "").substring(0, 5), "HH:mm"),
							endTime: DateTime.fromFormat((newLocationData.shift_end ?? "").substring(0, 5), "HH:mm"),
						})
					],
			};
			shouldAddLocationData = true;
		}

		// Re-check filters against the resolved location (mirrors buildScheduleData).
		const passesRoleOrJobFilter = jobManagementEnabled
			? !locationData.location.jobId || selectedJobFilters.includes(locationData.location.jobId)
			: locationData.location.roles.includes("all") ||
				locationData.location.roles
					.filter((r) => r !== "all")
					.some((role) => selectedRoleFilters.includes(NurseRole[NurseFullRoleShort[role as keyof typeof NurseFullRoleShort]]));
		const passesLocationFilter = selectedLocationFilters.length === 0 || selectedLocationFilters.includes(locationData.location.location ?? "");
		if (!(passesRoleOrJobFilter && passesLocationFilter)) return;

		const isFutureShift = recurrenceStart > today;
		const longPto = nursePreferencesToDaysOffAndPto(foundNurse);

		let didPlaceAny = false;
		for (const block of locationData.weeklyShifts) {
			for (const dayData of block.days) {
				const cellDate = dayData.dayDate;
				if (recurrenceEnd && cellDate > recurrenceEnd) continue;
				if (!isConsecutiveWorkingDay({ date: cellDate, recurrenceStart, workingDaysAmount, restDaysAmount, timezone })) continue;

				const longPtoForShift = longPto.find((dayOff) =>
					dayOff.dateFrom && dayOff.dateTo
						? dayOff.dateTo.diff(dayOff.dateFrom, "days").days >= longPtoMinimumDays &&
							cellDate >= dayOff.dateFrom.startOf("day") &&
							cellDate <= dayOff.dateTo.endOf("day")
						: dayOff.dateFrom
							? cellDate > dayOff.dateFrom.startOf("day")
							: false,
				);

				dayData.recurringShiftsInCell.push({
					recurringShift,
					isFutureShift,
					isOptimisticallyAdded: false,
					longPtoForShift: longPtoForShift ? { ptoStartDate: longPtoForShift.dateFrom, ptoEndDate: longPtoForShift.dateTo } : undefined,
					isCredentialed: true,
				});
				dayData.recurringShiftsInCell.sort((a, b) =>
					getNurseFormattedName(a.recurringShift.nurseId).localeCompare(getNurseFormattedName(b.recurringShift.nurseId)),
				);
				didPlaceAny = true;
			}
		}

		if (shouldAddLocationData && didPlaceAny) currentData.push(locationData);
	};

	for (const shift of shifts ?? []) placeShift(shift);

	// Sort locations, order sections, and apply the nurse filter (shared with buildScheduleData).
	const ordered = finalizeSectionMap({ data, masterScheduleSortState, customStringOrderMap, sectionOrder, nurseFilterId });

	return { data: ordered, placementErrors };
};
